import {gsap} from 'gsap';
import {SlotGame} from '@nolimitcity/slot-game/bin/SlotGame';
import {AssetLoader} from '@nolimitcity/slot-game/bin/asset/AssetLoader';
import {AssetsConfig} from '@nolimitcity/slot-game/bin/asset/AssetsConfig';
import {ResourcesGroupName} from '@nolimitcity/slot-game/bin/asset/ResourcesGroupName';
import {FlowState} from '@nolimitcity/slot-game/bin/flowcontrol/FlowState';
import {FlowStatePresentation} from '@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation';
import {GameFlowController} from '@nolimitcity/slot-game/bin/flowcontrol/GameFlowController';
import {GameMode} from '@nolimitcity/slot-game/bin/flowcontrol/GameMode';
import {NolimitApplication} from '@nolimitcity/slot-launcher/bin/NolimitApplication';
import {NolimitPlugin} from '@nolimitcity/slot-launcher/bin/NolimitPlugin';
import {FontLoader} from '@nolimitcity/slot-launcher/bin/loader/FontLoader';
import {TemplateAsset, TemplateLoader} from '@nolimitcity/slot-launcher/bin/loader/TemplateLoader';
import {OpenSans} from '@nolimitcity/slot-launcher/bin/loader/font/OpenSans';
import {LogLevel, Logger} from '@nolimitcity/slot-launcher/bin/utils/Logger';
import {setAssetsLoader} from '../core/component/AbstractSpine';
import {TestTool} from '../core/debug/DataChecker/Internal/TestTool';
import {Speedster} from '../core/debug/Speedster';
import {UniversalPlayer} from '../core/displayobject/UniversalPlayer';
import {ISymbolValues} from '../core/extra/MiniPayTable';
import {PresentationController} from '../core/gameflow/controller/PresentationController';
import {PresentationManager} from '../core/gameflow/controller/PresentationManager';
import {GameSetting} from '../core/setting/GameSetting';
import {Translator} from '../core/setting/Translator';
import {GraveSnatchers} from './Font/GraveSnatchers';
import {GameAssets} from './GameAssets';
import {GameConfig} from './config/base/GameConfig';
import {BaseMultiplier} from './modules/BaseMultiplier';
import {PosterManager} from './modules/Poster/PosterManager';
import {OverlayAnimation} from './modules/animations/OverlayAnimation';
import {RopeCutter} from './modules/animations/RopeCutter';
import {Background} from './modules/background/Background';
import {BackgroundOverlay} from './modules/background/BackgroundOverlay';
import {CountDisplay} from './modules/counter/CountDisplay';
import {SplashController} from './modules/splashScreen/SplashController';
import {SFSPickAndClickPresentation} from './presentations/feature/SFSPickAndClickPresentation';
import {SFSPickAndClickResultPresentation} from './presentations/feature/SFSPickAndClickResultPresentation';
import {SoulWildController} from './presentations/feature/SoulWildController';
import {SoulWildResultPresentation} from './presentations/feature/SoulWildResultPresentation';
import {SoulWildSouls} from './presentations/feature/SoulWildSouls';
import {SFSIntroPresentation} from './presentations/freespin/SFSIntroPresentation';
import {WinCapPresentation} from './presentations/maxwin/WinCapPresentation';
import {AvalancheStartPresentation} from './presentations/reel/general/AvalancheStartPresentation';
import {StartPresentation} from './presentations/reel/general/StartPresentation';
import { StopPresentation } from './presentations/reel/general/StopPresentation';
import {RestorePresentation} from './presentations/restore/RestorePresentation';
import {BigWinPresentation} from './presentations/win/BigWinPresentation';
import {LevelUpgradeView} from './presentations/win/LevelUpgradeView';
import {NormalWinPresentation} from './presentations/win/NormalWinPresentation';
import {ZeroBetPresentation} from './presentations/zero/ZeroBetPresentation';
import {CollectorBar} from './reels/CollectorBar';
import {BeheadedEngineManager} from './reels/BeheadedEngineManager';
import {BeheadedSymbolData} from './reels/symbol/BeheadedSymbolData';
import {BaseModel} from './server/base/BaseModel';
import {BeheadedSound} from './sound/BeheadedSound';
import {BeheadedConfirmPopupView} from './view/BeheadedConfirmPopupView';
import {BeheadedCharacterBackground} from './modules/background/BeheadedCharacterBackground';
import {BigWinCountDisplay} from './modules/counter/BigWinCountDisplay';
import { IntroPresentation } from './presentations/IntroPresentation';
import { IdlePresentation } from './presentations/Idle/IdlePresentation';
import { ParticlesManager } from './modules/particles/ParticlesManager';
import {Skinner} from "@nolimitcity/slot-launcher/bin/utils/Skinner";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";

export class Beheaded extends SlotGame {
    public static model: BaseModel;
    public static gameSetting: GameSetting;
    public static translate: Translator;
    private static presentationManager: PresentationManager;
    public static assetLoader: AssetLoader;
    public static soundPlayer: BeheadedSound;
    public static background: Background;
    public static overlayAnim: OverlayAnimation;
    public static ropeCutter: RopeCutter;
    public static gameFlowController: GameFlowController;
    public static symbolData: BeheadedSymbolData;
    public static counter: CountDisplay;
    public static bigWinCounter: BigWinCountDisplay;
    public static reelEngineManager: BeheadedEngineManager;
    public static collectorBar: CollectorBar;
    public static souldWildController: SoulWildController;
    public static overlayBG: BackgroundOverlay;
    public static baseMultiplier: BaseMultiplier;
    public static souldWildSouls: SoulWildSouls;
    public static levelUpgrade: LevelUpgradeView;
    public static confirmPopupView: BeheadedConfirmPopupView;

    public static parti: ParticlesManager;

    public static poster: PosterManager;
    public static splashWindow: SplashController;
    public static backgroundCharacter: BeheadedCharacterBackground;
    public static isSFSFinalScreenEnabled: boolean = false;
    private skinner:Skinner;
    constructor() {
        super();
        const validSkins:string[] = [
            "sweep"
        ]
        this.skinner = new Skinner(validSkins);
    }

    getNoWinGameData(): any {
        return GameConfig.NO_WIN_GAME_DATA;
    }

    public init(): Promise<NolimitPlugin> {

        return super.init().then((value: NolimitPlugin) => {
            Beheaded.translate = new Translator(Beheaded.apiPlugin.translations.translate);
            Beheaded.gameSetting = new GameSetting(Beheaded.apiPlugin);
            Beheaded.model = new BaseModel(Beheaded.apiPlugin);
            Beheaded.model.logger.level = LogLevel.DEV;

            Beheaded.gameFlowController = new GameFlowController(Beheaded.apiPlugin, Beheaded.model);
            Beheaded.gameFlowController.flowStateLogger.level = LogLevel.DEV;

            Beheaded.presentationManager = new PresentationManager(Beheaded.model);

            // const normalIdle = new NormalIdlePresentation();
            const intro = new IntroPresentation();
            const normalWin = new NormalWinPresentation();
            const stopReels = new StopPresentation();
            const bigWin = new BigWinPresentation();
            const reelAvalancheStart = new AvalancheStartPresentation();
            const soulWildResult = new SoulWildResultPresentation();
            const sfsIntro = new SFSIntroPresentation();
            const winCap = new WinCapPresentation();
            const sfsPickAndClick = new SFSPickAndClickPresentation();
            const sfsPickAndClickResult = new SFSPickAndClickResultPresentation();

            Beheaded.gameFlowController.addGameModeFlowStatePresentationMap(
                GameMode.NORMAL,
                new Map<FlowState, PresentationController>([
                    [FlowState.IDLE, Beheaded.presentationManager.addController([ new RestorePresentation(), intro, new IdlePresentation()], GameMode.NORMAL)],

                    [FlowState.BET, Beheaded.presentationManager.addController([new StartPresentation() ], GameMode.NORMAL)],
                    [FlowState.RESULT, Beheaded.presentationManager.addController([stopReels, soulWildResult], GameMode.NORMAL)],
                    [
                        FlowState.WIN,
                        Beheaded.presentationManager.addController(
                            [normalWin, bigWin, winCap, reelAvalancheStart,sfsIntro ],
                            GameMode.NORMAL
                        ),
                    ],
                ])
            );

            Beheaded.gameFlowController.addGameModeFlowStatePresentationMap(
                'NORMAL_AVALANCHE',
                new Map<FlowState, PresentationController>([
                    [
                        FlowState.IDLE,
                        Beheaded.presentationManager.addController([new RestorePresentation(), new ZeroBetPresentation()], 'NORMAL_AVALANCHE'),
                    ],
                    [FlowState.BET, Beheaded.presentationManager.addController([], 'NORMAL_AVALANCHE')],

                    [
                        FlowState.RESULT,
                        Beheaded.presentationManager.addController(
                            [stopReels, soulWildResult],
                            'NORMAL_AVALANCHE'
                        ),
                    ],
                    [
                        FlowState.WIN,
                        Beheaded.presentationManager.addController(
                            [normalWin, bigWin, winCap, reelAvalancheStart, ],
                            'NORMAL_AVALANCHE'
                        ),
                    ],
                ])
            );

            Beheaded.gameFlowController.addGameModeFlowStatePresentationMap(
                GameMode.FREESPIN,
                new Map<FlowState, PresentationController>([
                    [
                        FlowState.IDLE,
                        Beheaded.presentationManager.addController([new RestorePresentation(),new ZeroBetPresentation()], GameMode.FREESPIN),
                    ],

                    [FlowState.BET, Beheaded.presentationManager.addController([new StartPresentation()], GameMode.FREESPIN)],

                    [
                        FlowState.RESULT,
                        Beheaded.presentationManager.addController([stopReels], GameMode.FREESPIN),
                    ],
                    [
                        FlowState.WIN,
                        Beheaded.presentationManager.addController(
                            [normalWin, bigWin, winCap, reelAvalancheStart, ],
                            GameMode.FREESPIN
                        ),
                    ],
                ])
            );

            Beheaded.gameFlowController.addGameModeFlowStatePresentationMap(
                'FREESPIN_AVALANCHE',
                new Map<FlowState, PresentationController>([
                    [
                        FlowState.IDLE,
                        Beheaded.presentationManager.addController([new RestorePresentation(), new ZeroBetPresentation()], 'FREESPIN_AVALANCHE'),
                    ],
                    [FlowState.BET, Beheaded.presentationManager.addController([], 'FREESPIN_AVALANCHE')],

                    [
                        FlowState.RESULT,
                        Beheaded.presentationManager.addController(
                            [stopReels, soulWildResult],
                            'FREESPIN_AVALANCHE'
                        ),
                    ],

                    [
                        FlowState.WIN,
                        Beheaded.presentationManager.addController(
                            [normalWin, bigWin, winCap, reelAvalancheStart, ],
                            'FREESPIN_AVALANCHE'
                        ),
                    ],
                ])
            );

            Beheaded.gameFlowController.addGameModeFlowStatePresentationMap(
                'SUPER_FREESPIN',
                new Map<FlowState, PresentationController>([
                    [
                        FlowState.IDLE,
                        Beheaded.presentationManager.addController([new RestorePresentation(), new ZeroBetPresentation()], 'SUPER_FREESPIN'),
                    ],

                    [FlowState.BET, Beheaded.presentationManager.addController([new StartPresentation()], 'SUPER_FREESPIN')],

                    [
                        FlowState.RESULT,
                        Beheaded.presentationManager.addController([stopReels, soulWildResult], 'SUPER_FREESPIN'),
                    ],

                    [
                        FlowState.WIN,
                        Beheaded.presentationManager.addController(
                            [normalWin, bigWin, winCap, reelAvalancheStart, ],
                            'SUPER_FREESPIN'
                        ),
                    ],
                ])
            );

            Beheaded.gameFlowController.addGameModeFlowStatePresentationMap(
                'SUPER_FREESPIN_AVALANCHE',
                new Map<FlowState, PresentationController>([
                    [
                        FlowState.IDLE,
                        Beheaded.presentationManager.addController(
                            [new RestorePresentation(), new ZeroBetPresentation()],
                            'SUPER_FREESPIN_AVALANCHE'
                        ),
                    ],
                    [FlowState.BET, Beheaded.presentationManager.addController([], 'SUPER_FREESPIN_AVALANCHE')],

                    [
                        FlowState.RESULT,
                        Beheaded.presentationManager.addController(
                            [stopReels, soulWildResult],
                            'SUPER_FREESPIN_AVALANCHE'
                        ),
                    ],

                    [
                        FlowState.WIN,
                        Beheaded.presentationManager.addController(
                            [normalWin, bigWin, winCap, reelAvalancheStart, ],
                            'SUPER_FREESPIN_AVALANCHE'
                        ),
                    ],
                ])
            );

            Beheaded.gameFlowController.addGameModeFlowStatePresentationMap(
                'SUPER_FREESPIN_END',
                new Map<FlowState, PresentationController>([
                    [FlowState.IDLE, Beheaded.presentationManager.addController([new RestorePresentation(),sfsPickAndClick], 'SUPER_FREESPIN_END')],
                    [
                        FlowState.RESULT,
                        Beheaded.presentationManager.addController([sfsPickAndClickResult], 'SUPER_FREESPIN_END'),
                    ],
                    [FlowState.WIN, Beheaded.presentationManager.addController([winCap], 'SUPER_FREESPIN_END')],
                ])
            );

            Beheaded.gameFlowController.addGameModeFlowStatePresentationMap(
                GameMode.GAMBLE,
                new Map<FlowState, FlowStatePresentation>([
                    [FlowState.IDLE, Beheaded.presentationManager.addController([normalWin, bigWin], GameMode.GAMBLE)],
                ])
            );


            //Making sure that skinID is set based  on "replaceWordFree" gcc.
            if (SlotGame.apiPlugin.gameClientConfiguration.isSet){
                if (SlotGame.apiPlugin.gameClientConfiguration.sweepGfx){
                    this.skinner.skinId = "sweep";
                }
            }
            else {
                SlotGame.apiPlugin.events.on(APIEvent.GAME_CLIENT_CONFIGURATION_APPLIED,()=>{
                    if (SlotGame.apiPlugin.gameClientConfiguration.sweepGfx){
                        this.skinner.skinId = "sweep";
                    }
                })
            }
            return value;
        });
    }

    private paytable:string = require("../../nolimit/screens/paytable.mustache");
    public getPaytable(): Promise<string> {
        return new Promise((resolve, reject) => {
            let symbolDataValueData: ISymbolValues = {
                W: {3: 5, 4: 10, 5: 20},
                L1: {3: 5, 4: 10, 5: 20},
                L2: {3: 5, 4: 10, 5: 20},
                L3: {3: 4, 4: 8, 5: 15},
                L4: {3: 3, 4: 6, 5: 15},
                M1: {3: 15, 4: 30, 5: 80},
                M2: {3: 10, 4: 20, 5: 60},
                M3: {3: 9, 4: 18, 5: 40},
                M4: {3: 8, 4: 16, 5: 25},
            };
            if (Beheaded.model.initData.symbolValues) {
                symbolDataValueData = Beheaded.model.initData.symbolValues;
                Logger.logDev('Using symbolValues from INIT data');
            } else {
                Logger.logDev('Using default symbolValues');
            }

            const data = {
                skinPath: "screens/images/"
            };

            if (this.skinner.skinId != undefined) {
                data.skinPath = `../resources/images/${this.skinner.skinId}/`;
            }
            let paytableString: string = (<any>this.paytable).text;
            paytableString = paytableString.replace(/{{skinPath}}/g, data.skinPath);
            for (let key in symbolDataValueData) {
                paytableString = paytableString.replace(
                    `{{${key}}}`,
                    Beheaded.getSymbolString(key, symbolDataValueData[key])
                );
            }
            resolve(paytableString);
        });
    }

    private static getSymbolString(key: string, data: { [numSymbols: number]: number }): string {
        let symbol: string = '';
        if (key !== 'W') {
            symbol = `<div class="symbol">
            <img src="{{gamePath}}screens/images/symbols/${key}.png">
            <div class="payout-group">
                <div class="payout">
                    <span class="number">5</span>
                    <span class="multiplier" data-multiplier="${data[5] * 0.05}">${data[5] * 0.05}</span>
                </div>
                <div class="payout">
                    <span class="number">4</span>
                    <span class="multiplier" data-multiplier="${data[4] * 0.05}">${data[4] * 0.05}</span>
                </div>
                <div class="payout">
                    <span class="number">3</span>
                    <span class="multiplier" data-multiplier="${data[3] * 0.05}">${data[3] * 0.05}</span>
                </div>
            </div>
            </div>`;
        }
        return symbol;
    }

    public getReady(): Promise<NolimitPlugin> {
        //Show intro and load game assets
        return super.getReady().then((value: NolimitPlugin) => {
            const fontLoader = new FontLoader(NolimitApplication.resourcePath);
            fontLoader.add(OpenSans.ITALIC_600);
            fontLoader.add(OpenSans.NORMAL_300);
            fontLoader.add(OpenSans.NORMAL_400);
            fontLoader.add(OpenSans.NORMAL_700);
            fontLoader.add(GraveSnatchers.NORMAL_400);

            AssetsConfig.addToAssetsConfig(GameAssets.RESOURCES_GROUPS);
            Beheaded.assetLoader = new AssetLoader(Beheaded.apiPlugin, NolimitApplication.resourcePath + '/resources/');
            setAssetsLoader(Beheaded.assetLoader);
            const allPromises = [fontLoader.load(), Beheaded.assetLoader.load(ResourcesGroupName.MAIN)]
            if (this.skinner.skinId != undefined) {
                const replaceMap:Map<string, string> = new Map();
                //Bonus
                replaceMap.set("pageImage_7", "pageImage_7.png")
                replaceMap.set("BOOSTED_BET", "BOOSTED_BET.png");
                allPromises.push(this.skinner.loadSkin(replaceMap));
            }
            const promises = Promise.all(allPromises);

            return promises.then((values: any) => {
                UniversalPlayer.setLoader(Beheaded.assetLoader);

                (Beheaded.symbolData = new BeheadedSymbolData(
                    Beheaded.assetLoader,
                    GameConfig.DEFAULT_SYMBOL_DIM
                )).parseInitDataForInit(Beheaded.model.initData);

                Beheaded.presentationManager.initPresentations();
                this.createViewsAndComponents();

                return value;
            });
        });
    }

    public getReadyToStart(): Promise<NolimitPlugin> {
        //Game assets loaded, waiting for user to start game.
        return super.getReadyToStart().then((value: NolimitPlugin) => {
            return value;
        });
    }

    public start(): Promise<NolimitPlugin> {
        //User has pressed continue and the game starts.
        return super.start().then((value: NolimitPlugin) => {
            Beheaded.gameFlowController.start();
            return value;
        });
    }

    private createViewsAndComponents() {
        if (GameSetting.isDevMode) {
            Logger.logDev('SymbolData: ', BeheadedSymbolData.getAllConfigs());
            this.addLayer(new Speedster());
            this.addLayer(new TestTool(Beheaded.model));
        }

        Beheaded.reelEngineManager = new BeheadedEngineManager(
            5,
            GameConfig.REEL_ENGINE_CONFIG,
            Beheaded.model.initData.Reel,
            (value: number) => Beheaded.gameFlowController.calculatePaddingDuration(value) // ReelManager should NOT have dependencies to GameflowController.
        );

        Beheaded.soundPlayer = new BeheadedSound();

        Beheaded.soundPlayer.switchMusic(Beheaded.model.data.mode);

        Beheaded.background = new Background();
        Beheaded.backgroundCharacter = new BeheadedCharacterBackground();
        Beheaded.baseMultiplier = new BaseMultiplier();
        Beheaded.overlayBG = new BackgroundOverlay();
        
        Beheaded.counter = new CountDisplay();
        Beheaded.bigWinCounter = new BigWinCountDisplay();
        Beheaded.overlayAnim = new OverlayAnimation();
        Beheaded.collectorBar = new CollectorBar(Beheaded.model.initData.gameLevels);
        Beheaded.souldWildController = new SoulWildController();
        Beheaded.ropeCutter = new RopeCutter();
        Beheaded.souldWildSouls = new SoulWildSouls();
        Beheaded.confirmPopupView = new BeheadedConfirmPopupView();
        Beheaded.poster = new PosterManager();
        Beheaded.splashWindow = new SplashController();

        Beheaded.parti = new ParticlesManager(Beheaded.assetLoader);

        this.addLayer(Beheaded.confirmPopupView);
        this.addLayer(Beheaded.bigWinCounter);
        this.addLayer(Beheaded.parti);
        this.addLayer(Beheaded.counter);
        this.addLayer(Beheaded.splashWindow);
        this.addLayer(Beheaded.poster);
        this.addLayer(Beheaded.overlayAnim);
        this.addLayer(Beheaded.ropeCutter);
        this.addLayer(Beheaded.collectorBar);
        this.addLayer(Beheaded.souldWildSouls);
        this.addLayer(Beheaded.souldWildController);
        this.addLayer(Beheaded.baseMultiplier);
        this.addLayer(Beheaded.overlayBG);
        this.addLayer(Beheaded.reelEngineManager);
        this.addLayer(Beheaded.backgroundCharacter);
        this.addLayer(Beheaded.background);

        if (Beheaded.model.initData.isRestoreState) {
            IntroPresentation.introPlayed = true;
            
            let currentLevel: number = Beheaded.model.initData.isAnAvalanche ? Beheaded.model.initData.currentLevel : Beheaded.model.initData.nextLevel;
            SlotGame.keypadPlugin.setWin(
                Beheaded.model.initData.raw.accumulatedRoundWin,
                true,
                Beheaded.model.initData.Win.isWinBelowStake()
            );
            Beheaded.backgroundCharacter.restorePlayLevelUpSounds(currentLevel);
        }

        // correction for the stageLayer - fix this in the framework later
        Beheaded.bigWinCounter.parent.pivot.set(360,360);
        Beheaded.bigWinCounter.parent.position.set(360,360);
    }

    private addLayer(layer: any, order: number = 0): any {
        NolimitApplication.addLayerAt(layer.constructor.name, layer, order);
        return layer;
    }
}
